// Core logging types and interfaces for the AlphaMind IDO Frontend logging system

// Core log event interface
export interface LogEvent {
  id: string;
  sessionId: string;
  timestamp: number;
  eventType: LogEventType;
  category: LogCategory;
  action: string;
  userId?: string;
  walletAddress?: string;
  data?: Record<string, any>;
  error?: LogError;
  context: LogContext;
}

// Error information interface
export interface LogError {
  message: string;
  stack?: string;
  name: string;
  code?: string | number;
}

// Context information interface
export interface LogContext {
  url: string;
  referrer?: string;
  userAgent: string;
  app: AppBuildInfo;
  device: DeviceInfo;
  network: NetworkInfo;
  wallet: WalletInfo;
  performance: PerformanceInfo;
}

export interface AppBuildInfo {
  version: string;
  buildSha: string;
  environment?: string;
}

// Device information interface
export interface DeviceInfo {
  type: DeviceType;
  os: string;
  browser: string;
  browserVersion: string;
  screenResolution: string;
  viewportSize: string;
  language: string;
  timezone: string;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

// Network information interface
export interface NetworkInfo {
  chainId?: number;
  networkName?: string;
  isConnected: boolean;
  connectionType?: string;
  effectiveType?: string;
}

// Wallet information interface
export interface WalletInfo {
  connectedWallets: WalletType[];
  primaryWallet?: WalletType;
  hasMultipleWallets: boolean;
  providerConflicts: string[];
  walletVersions: Record<string, string>;
}

// Performance information interface
export interface PerformanceInfo {
  memoryUsage?: number;
  connectionSpeed?: string;
  renderTime?: number;
  loadTime?: number;
}

// Event types enum
export enum LogEventType {
  WALLET_CONNECTION = 'wallet_connection',
  AUTHENTICATION = 'authentication',
  IDO_INTERACTION = 'ido_interaction',
  PURCHASE = 'purchase',
  ERROR = 'error',
  PAGE_VIEW = 'page_view',
  USER_ACTION = 'user_action',
  PERFORMANCE = 'performance'
}

// Category enum
export enum LogCategory {
  CONNECTION = 'connection',
  AUTH = 'auth',
  TRANSACTION = 'transaction',
  NAVIGATION = 'navigation',
  ERROR = 'error',
  PERFORMANCE = 'performance',
  USER_INTERACTION = 'user_interaction'
}

// Action types for different categories
export enum LogAction {
  // Connection actions
  CONNECTION_ATTEMPT = 'connection_attempt',
  CONNECTION_SUCCESS = 'connection_success',
  CONNECTION_ERROR = 'connection_error',
  CONNECTION_RETRY = 'connection_retry',
  PROVIDER_CONFLICT = 'provider_conflict',
  WALLET_DETECTED = 'wallet_detected',
  
  // Authentication actions
  LOGIN_ATTEMPT = 'login_attempt',
  LOGIN_SUCCESS = 'login_success',
  LOGIN_ERROR = 'login_error',
  LOGOUT = 'logout',
  MESSAGE_SIGN = 'message_sign',
  NETWORK_SWITCH = 'network_switch',
  
  // IDO interaction actions
  IDO_PAGE_VIEW = 'ido_page_view',
  IDO_DETAILS_VIEW = 'ido_details_view',
  WHITELIST_ATTEMPT = 'whitelist_attempt',
  WHITELIST_SUCCESS = 'whitelist_success',
  WHITELIST_ERROR = 'whitelist_error',
  
  // Purchase actions
  PURCHASE_ATTEMPT = 'purchase_attempt',
  PURCHASE_SUCCESS = 'purchase_success',
  PURCHASE_ERROR = 'purchase_error',
  PURCHASE_CANCEL = 'purchase_cancel',
  
  // Wallet association actions
  ASSOCIATION_ATTEMPT = 'association_attempt',
  ASSOCIATION_CHECK = 'association_check',
  
  // Error actions
  JAVASCRIPT_ERROR = 'javascript_error',
  VALIDATION_ERROR = 'validation_error',
  NETWORK_ERROR = 'network_error',
  REACT_ERROR_BOUNDARY = 'react_error_boundary',
  
  // User action types
  BUTTON_CLICK = 'button_click',
  FORM_SUBMIT = 'form_submit',
  MODAL_OPEN = 'modal_open',
  MODAL_CLOSE = 'modal_close',
  SCROLL = 'scroll',
  
  // Page view actions
  PAGE_LOAD = 'page_load',
  PAGE_UNLOAD = 'page_unload',
  ROUTE_CHANGE = 'route_change',
  
  // Performance actions
  COMPONENT_MOUNT = 'component_mount',
  COMPONENT_UNMOUNT = 'component_unmount',
  COMPONENT_RERENDER = 'component_rerender',
  API_REQUEST = 'api_request',
  API_RESPONSE = 'api_response'
}

// Wallet types
export type WalletType = 'metamask' | 'walletconnect' | 'coinbase' | 'okx' | 'phantom' | 'solflare' | 'solana' | 'tonkeeper' | 'ton' | 'unknown';

// Device types
export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'unknown';

// Configuration interface
export interface LoggingConfig {
  endpoint: string;
  batchSize: number;
  flushInterval: number;
  enabled: boolean;
  enabledCategories: LogCategory[];
  enabledEventTypes: LogEventType[];
  consoleEnabled: boolean;
  retryAttempts: number;
  retryDelay: number;
}

// Batch request interface
export interface LogBatchRequest {
  logs: LogEvent[];
}

// Batch response interface
export interface LogBatchResponse {
  success: boolean;
  processed?: number;
  error?: string;
  details?: string[];
  retryAfter?: number;
}

// Custom error types
export class ValidationError extends Error {
  constructor(message: string, public field: string, public value: any) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class NetworkError extends Error {
  constructor(message: string, public endpoint: string, public statusCode?: number) {
    super(message);
    this.name = 'NetworkError';
  }
}

export class LoggingError extends Error {
  constructor(message: string, public originalError?: Error) {
    super(message);
    this.name = 'LoggingError';
  }
}

// Hook return types
export interface WalletConnectionLoggerHook {
  logConnectionAttempt: (walletType: WalletType, data?: Record<string, any>) => void;
  logConnectionSuccess: (walletType: WalletType, data?: Record<string, any>) => void;
  logConnectionError: (walletType: WalletType, error: Error, data?: Record<string, any>) => void;
  logProviderConflict: (conflictingProviders: string[], data?: Record<string, any>) => void;
  logWalletDetected: (walletType: WalletType, data?: Record<string, any>) => void;
}

export interface PurchaseLoggerHook {
  logPurchaseAttempt: (idoId: string, amount: string, currency: string, data?: Record<string, any>) => void;
  logPurchaseSuccess: (idoId: string, amount: string, currency: string, txHash: string, data?: Record<string, any>) => void;
  logPurchaseError: (idoId: string, amount: string, currency: string, error: Error, data?: Record<string, any>) => void;
  logPurchaseCancel: (idoId: string, reason: string, data?: Record<string, any>) => void;
}

export interface ErrorLoggerHook {
  logJavaScriptError: (error: Error, component?: string, action?: string, data?: Record<string, any>) => void;
  logValidationError: (error: ValidationError, field: string, value: any, data?: Record<string, any>) => void;
  logNetworkError: (error: NetworkError, endpoint: string, data?: Record<string, any>) => void;
}

export interface UserActionLoggerHook {
  logButtonClick: (buttonId: string, component: string, data?: Record<string, any>) => void;
  logFormSubmit: (formId: string, component: string, data?: Record<string, any>) => void;
  logModalOpen: (modalId: string, trigger: string, data?: Record<string, any>) => void;
  logModalClose: (modalId: string, trigger: string, data?: Record<string, any>) => void;
}

export interface PageViewLoggerHook {
  logPageView: (data?: Record<string, any>) => void;
  logCustomPageView: (path: string, title: string, data?: Record<string, any>) => void;
  logRouteChange: (from: string, to: string, data?: Record<string, any>) => void;
}

export interface IdoLoggerHook {
  logIdoPageView: (idoId: string, idoTitle: string, data?: Record<string, any>) => void;
  logIdoDetailsView: (idoId: string, section: string, data?: Record<string, any>) => void;
  logWhitelistAttempt: (idoId: string, data?: Record<string, any>) => void;
  logWhitelistSuccess: (idoId: string, data?: Record<string, any>) => void;
  logWhitelistError: (idoId: string, error: Error, data?: Record<string, any>) => void;
}

export interface AuthLoggerHook {
  logLoginAttempt: (walletType: WalletType, data?: Record<string, any>) => void;
  logLoginSuccess: (walletType: WalletType, userId: string, data?: Record<string, any>) => void;
  logLoginError: (walletType: WalletType, error: Error, data?: Record<string, any>) => void;
  logLogout: (userId: string, data?: Record<string, any>) => void;
  logMessageSign: (walletType: WalletType, success: boolean, data?: Record<string, any>) => void;
  logNetworkSwitch: (fromChainId: number, toChainId: number, success: boolean, data?: Record<string, any>) => void;
}

export interface NetworkLoggerHook {
  logNetworkChange: (chainId: number, networkName: string, data?: Record<string, any>) => void;
  logConnectionStatusChange: (isConnected: boolean, data?: Record<string, any>) => void;
}

// Performance metric interface
export interface PerformanceMetric {
  name: string;
  value: number;
  unit: string;
  component?: string;
  timestamp?: number;
}
